/**
 * Desmond Save Bridge
 * Reads/writes Desmond's localforage IndexedDB saves, enabling cloud sync.
 *
 * Desmond stores saves via localforage (bundled in desmond.js):
 *   Database: "localforage", Store: "keyvaluepairs"
 *   Key: "sav-[gameID]", Value: Uint8Array (raw save binary)
 *
 * gameID = first 16 bytes of ROM header as ASCII (null→space).
 * If byte 0xC is '#', it's homebrew and gameID = filename.
 * Source: desmond.js lines 3051-3057
 */

const LOCALFORAGE_DB = 'localforage';
const LOCALFORAGE_STORE = 'keyvaluepairs';

/**
 * Compute Desmond's gameID from a ROM buffer.
 * Must match desmond.js lines 3051-3057 exactly.
 */
export function computeGameID(romBuffer: ArrayBuffer, fileName: string): string {
  const header = new Uint8Array(romBuffer, 0, Math.min(0x10, romBuffer.byteLength));
  let gameID = '';
  for (let i = 0; i < 0x10; i++) {
    const byte = i < header.length ? header[i] : 0;
    gameID += byte === 0 ? ' ' : String.fromCharCode(byte);
  }
  if (gameID.charCodeAt(0xC) === 0x23) { // '#' = homebrew
    gameID = fileName;
  }
  return gameID;
}

function openLocalforageDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(LOCALFORAGE_DB);
    request.onerror = () => reject(new Error('Failed to open localforage DB'));
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Ensure localforage DB and store exist. Creates them if necessary.
 * This allows restoring cloud saves even on first launch (before Desmond has created the DB).
 */
function ensureLocalforageDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // First, try to open the DB to check if the store exists
    const checkRequest = indexedDB.open(LOCALFORAGE_DB);

    checkRequest.onerror = () => reject(new Error('Failed to open localforage DB'));

    checkRequest.onsuccess = () => {
      const db = checkRequest.result;

      // If store exists, we're done
      if (db.objectStoreNames.contains(LOCALFORAGE_STORE)) {
        resolve(db);
        return;
      }

      // Store doesn't exist — close and reopen with version bump to create it
      const currentVersion = db.version;
      db.close();

      const upgradeRequest = indexedDB.open(LOCALFORAGE_DB, currentVersion + 1);

      upgradeRequest.onerror = () => reject(new Error('Failed to create localforage store'));

      upgradeRequest.onupgradeneeded = (event: any) => {
        const upgradedDB = event.target.result as IDBDatabase;
        if (!upgradedDB.objectStoreNames.contains(LOCALFORAGE_STORE)) {
          upgradedDB.createObjectStore(LOCALFORAGE_STORE);
          console.log('✅ Created localforage store (for cloud save restore)');
        }
      };

      upgradeRequest.onsuccess = () => resolve(upgradeRequest.result);
    };
  });
}

/**
 * Read save data from Desmond's localforage.
 * Returns null if no save exists.
 */
export async function readDesmondSave(gameID: string): Promise<Uint8Array | null> {
  let db: IDBDatabase | null = null;
  try {
    db = await openLocalforageDB();
    if (!db.objectStoreNames.contains(LOCALFORAGE_STORE)) {
      return null;
    }
    return await new Promise((resolve, reject) => {
      const tx = db!.transaction(LOCALFORAGE_STORE, 'readonly');
      const store = tx.objectStore(LOCALFORAGE_STORE);
      const req = store.get('sav-' + gameID);
      req.onsuccess = () => {
        const result = req.result;
        if (!result) {
          resolve(null);
        } else if (result instanceof Uint8Array) {
          resolve(result);
        } else if (result instanceof ArrayBuffer) {
          resolve(new Uint8Array(result));
        } else {
          // localforage may wrap in various types
          resolve(new Uint8Array(result));
        }
      };
      req.onerror = () => reject(new Error('Failed to read save from localforage'));
    });
  } finally {
    db?.close();
  }
}

/**
 * Write save data into Desmond's localforage.
 * Desmond will auto-load this on next ROM load.
 * Creates the DB/store automatically if it doesn't exist (allows cloud restore on first launch).
 */
export async function writeDesmondSave(gameID: string, saveData: Uint8Array): Promise<void> {
  let db: IDBDatabase | null = null;
  try {
    db = await ensureLocalforageDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(LOCALFORAGE_STORE, 'readwrite');
      const store = tx.objectStore(LOCALFORAGE_STORE);
      const req = store.put(saveData, 'sav-' + gameID);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(new Error('Failed to write save to localforage'));
    });
  } finally {
    db?.close();
  }
}
